/**
 * A row of review quotes the customer scrolls sideways. Scroll snap does the
 * paging; this only keeps the dots in step and wires the arrow buttons.
 * No autoplay.
 */
class FaReviewCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.children);
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.index = 0;

    this.querySelectorAll('[data-step]').forEach((button) => {
      button.addEventListener('click', () => this.go(this.index + Number(button.getAttribute('data-step'))));
    });

    this.track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.track.addEventListener('keydown', this.#onKeydown);

    // With data-auto-height the track takes the height of the review on show,
    // so short reviews leave no gap. Re-measured whenever a review reflows.
    if (this.hasAttribute('data-auto-height')) {
      this.#resizeObserver = new ResizeObserver(() => this.#fitHeight());
      this.slides.forEach((slide) => this.#resizeObserver?.observe(slide));
    }

    this.#update(0);
    this.#loadPhotosWhenShown();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.#onScroll);
    this.track?.removeEventListener('keydown', this.#onKeydown);
    this.#resizeObserver?.disconnect();
    this.#openObserver?.disconnect();
  }

  /** @type {MutationObserver | undefined} */
  #openObserver;

  /**
   * The menus are on every page but closed, so the review photos wait in
   * data-src/data-srcset until the mega menu panel (loses `inert`) or the menu
   * drawer (gains `open`) first opens. Anywhere else they load straight away.
   */
  #loadPhotosWhenShown() {
    if (!this.querySelector('img[data-src]')) return;

    const panel = this.closest('.menu-list__submenu');
    const drawer = this.closest('details.menu-drawer-container');
    const isOpen = () => (panel ? !panel.inert : drawer ? drawer.open : true);

    if (isOpen()) {
      this.#loadPhotos();
      return;
    }

    const watched = panel ?? drawer;
    if (!watched) return;
    this.#openObserver = new MutationObserver(() => {
      if (!isOpen()) return;
      this.#openObserver?.disconnect();
      this.#loadPhotos();
    });
    this.#openObserver.observe(watched, { attributes: true, attributeFilter: ['inert', 'open'] });
  }

  #loadPhotos() {
    this.querySelectorAll('img[data-src]').forEach((img) => {
      if (!(img instanceof HTMLImageElement)) return;
      const show = () => img.setAttribute('data-loaded', '');
      img.addEventListener('load', show, { once: true });
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src ?? '';
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
      if (img.complete && img.naturalWidth > 0) show();
    });
  }

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  #fitHeight() {
    const slide = this.slides[this.index];
    if (!slide || !this.hasAttribute('data-auto-height')) return;
    const height = slide.offsetHeight;
    if (height > 0) this.track.style.height = `${height}px`;
  }

  /** @param {number} target */
  go(target) {
    const count = this.slides.length;
    const index = ((target % count) + count) % count;
    const slide = this.slides[index];
    const first = this.slides[0];
    if (!slide || !first) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: slide.offsetLeft - first.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.#update(index);
  }

  #frame = 0;

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const width = this.track.clientWidth || 1;
      this.#update(Math.round(Math.abs(this.track.scrollLeft) / width));
    });
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.go(this.index + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.go(this.index - 1);
    }
  };

  /** @param {number} index */
  #update(index) {
    this.index = Math.min(Math.max(index, 0), this.slides.length - 1);
    this.dots.forEach((dot, i) => dot.toggleAttribute('data-active', i === this.index));
    this.slides.forEach((slide, i) => slide.setAttribute('aria-hidden', String(i !== this.index)));
    this.#fitHeight();
  }
}

if (!customElements.get('fa-review-carousel')) {
  customElements.define('fa-review-carousel', FaReviewCarousel);
}
